import React, { useEffect, useRef, useState } from "react";
import AuthHero from "./AuthHero";

// Keep keyboard handling local to auth; never change body classes or app state.
export default function AuthLayout({ children, screen, toolbar, back }) {
  const page = useRef(null);
  const mounted = useRef(false);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const heading = page.current.querySelector(".mmt-login-card h2");
    const previousTitle = document.title;
    if (heading) {
      document.title = `${heading.textContent} · SeniorLife.AI`;
      if (mounted.current) heading.focus({ preventScroll: true });
    }
    mounted.current = true;
    setCompact(false);
    return () => {
      document.title = previousTitle;
    };
  }, [screen]);

  useEffect(() => {
    const element = page.current;
    const viewport = document.querySelector('meta[name="viewport"]');
    const originalViewport = viewport?.getAttribute("content");
    if (viewport && !originalViewport?.includes("interactive-widget=")) {
      viewport.setAttribute(
        "content",
        `${originalViewport || "width=device-width, initial-scale=1"}, interactive-widget=resizes-content`,
      );
    }
    let revealTimer;
    let blurTimer;
    const reveal = (input) => {
      window.clearTimeout(revealTimer);
      revealTimer = window.setTimeout(() => {
        input.scrollIntoView?.({
          block: "center",
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
            .matches
            ? "auto"
            : "smooth",
        });
      }, 120);
    };
    const focus = (event) => {
      if (
        event.target.tagName !== "INPUT" ||
        !window.matchMedia("(max-width: 640px), (max-height: 560px)").matches
      )
        return;
      window.clearTimeout(blurTimer);
      setCompact(true);
      reveal(event.target);
    };
    const blur = () => {
      blurTimer = window.setTimeout(() => {
        if (
          !element.contains(document.activeElement) ||
          document.activeElement?.tagName !== "INPUT"
        ) {
          setCompact(false);
        }
      }, 150);
    };
    const resize = () => {
      const input = document.activeElement;
      if (element.contains(input) && input?.tagName === "INPUT") reveal(input);
    };
    element.addEventListener("focusin", focus);
    element.addEventListener("focusout", blur);
    window.visualViewport?.addEventListener("resize", resize);
    return () => {
      if (viewport) {
        if (originalViewport === null) viewport.removeAttribute("content");
        else viewport.setAttribute("content", originalViewport);
      }
      window.clearTimeout(revealTimer);
      window.clearTimeout(blurTimer);
      element.removeEventListener("focusin", focus);
      element.removeEventListener("focusout", blur);
      window.visualViewport?.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <main
      ref={page}
      className={`mmt-login-page mmt-auth-page${compact ? " mmt-auth-compact" : ""}`}
    >
      {toolbar && <div className="mmt-login-lang">{toolbar}</div>}
      <section className="mmt-login-scroll">
        <div className="mmt-login-shell">
          {back}
          <AuthHero />
          {children}
        </div>
      </section>
    </main>
  );
}
